import React, { useState, useEffect, useRef } from 'react';
import { Bell, Send, Smartphone, CheckCircle2, XCircle } from 'lucide-react';
import { Button } from './ui/button';
import { supabase } from '../integrations/supabase/client';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../hooks/use-toast';
import { usePushNotifications } from '../hooks/usePushNotifications';
import PushNotificationManager from './PushNotificationManager';

const STORAGE_KEY = 'push_test_last_result';

type LastResult = {
  ok: boolean;
  message: string;
  raw?: any;
  at?: string;
};

export const PushTestPanel: React.FC = () => {
  const { user } = useAuth();
  const { toast } = useToast();
  const {
    isSupported,
    isSubscribed,
    isIOS,
    isStandalone,
  } = usePushNotifications();

  const [sending, setSending] = useState(false);
  const [lastResult, setLastResultState] = useState<LastResult | null>(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  });
  const resultRef = useRef<HTMLDivElement>(null);

  const setLastResult = (r: LastResult | null) => {
    setLastResultState(r);
    try {
      if (r) localStorage.setItem(STORAGE_KEY, JSON.stringify(r));
      else localStorage.removeItem(STORAGE_KEY);
    } catch {}
  };

  useEffect(() => {
    if (lastResult && resultRef.current) {
      resultRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [lastResult?.at]);

  const handleSendTest = async () => {
    if (!user?.id) {
      toast({
        title: 'Não autenticado',
        description: 'Faça login novamente para enviar o teste.',
        variant: 'destructive',
      });
      return;
    }

    setSending(true);
    setLastResult(null);

    let data: any = null;
    let error: any = null;

    try {
      const resp = await supabase.functions.invoke('send-push-notification', {
        body: {
          user_ids: [user.id],
          title: '🔔 Teste de Push — Fox Vendas',
          body: 'Se você está vendo isso, as notificações push estão funcionando!',
          url: '/dashboard',
          data: { type: 'test', sent_at: new Date().toISOString() },
        },
      });
      data = resp.data;
      error = resp.error;
    } catch (err: any) {
      error = err;
    }

    console.log('🔔 [PushTest] result:', { data, error });

    const sent = data?.sent ?? data?.successful ?? 0;
    const failed = data?.failed ?? 0;
    const total = data?.total ?? data?.subscriptions ?? 0;
    const ok = !error && sent > 0;

    const message = error
      ? `Erro ao chamar edge function: ${error.message || String(error)}`
      : ok
        ? `Push enviado para ${sent}/${total} dispositivo(s). Falhas: ${failed}.`
        : `Nenhum push entregue. Total: ${total}, Enviados: ${sent}, Falhas: ${failed}.`;

    setLastResult({
      ok,
      message,
      raw: data ?? { error: error?.message, context: (error as any)?.context },
      at: new Date().toISOString(),
    });

    toast({
      title: ok ? 'Teste enviado' : 'Falha no envio',
      description: ok
        ? 'Veja a tabela de resultados abaixo.'
        : 'Veja os detalhes no painel abaixo (role a página).',
      variant: ok ? 'default' : 'destructive',
    });

    setSending(false);
  };

  const handleResetSubscriptions = async () => {
    if (!user?.id) return;
    if (!confirm('Isso vai APAGAR todas as suas inscrições push no servidor e criar uma nova somente para este dispositivo. Continuar?')) {
      return;
    }
    setSending(true);
    try {
      // 1) Apaga TODAS as subscriptions do usuário no banco
      const { error: delErr } = await (supabase as any)
        .from('push_subscriptions')
        .delete()
        .eq('user_id', user.id);
      if (delErr) throw delErr;

      // 2) Cancela a subscription local do browser (se houver)
      if ('serviceWorker' in navigator) {
        const reg = await navigator.serviceWorker.ready;
        const sub = await reg.pushManager.getSubscription();
        if (sub) await sub.unsubscribe();
      }

      // 3) Cria uma nova subscription do zero usando o VAPID atual
      const { data: vapidResp, error: vapidErr } = await supabase.functions.invoke('get-vapid-keys');
      if (vapidErr) throw vapidErr;
      const vapidPublicKey = vapidResp?.publicKey;
      if (!vapidPublicKey) throw new Error('VAPID public key não encontrada');

      const reg = await navigator.serviceWorker.ready;
      const urlBase64ToUint8Array = (b64: string) => {
        const padding = '='.repeat((4 - (b64.length % 4)) % 4);
        const base64 = (b64 + padding).replace(/-/g, '+').replace(/_/g, '/');
        const raw = atob(base64);
        const out = new Uint8Array(raw.length);
        for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
        return out;
      };

      const newSub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(vapidPublicKey),
      });

      const toB64 = (buf: ArrayBuffer | null) =>
        buf ? btoa(String.fromCharCode(...new Uint8Array(buf))) : null;

      const { error: insErr } = await (supabase as any)
        .from('push_subscriptions')
        .insert({
          user_id: user.id,
          endpoint: newSub.endpoint,
          p256dh: toB64(newSub.getKey('p256dh')),
          auth: toB64(newSub.getKey('auth')),
          subscription_data: newSub.toJSON(),
        });
      if (insErr) throw insErr;

      toast({
        title: '✅ Re-inscrição concluída',
        description: 'Agora clique em "Enviar notificação de teste" para validar.',
      });
      setLastResult({
        ok: true,
        message: 'Subscriptions antigas apagadas. Nova subscription criada com sucesso.',
        raw: { endpoint: newSub.endpoint, provider: newSub.endpoint.includes('apple') ? 'apple' : 'fcm' },
        at: new Date().toISOString(),
      });
    } catch (err: any) {
      console.error('Erro ao re-inscrever:', err);
      toast({
        title: 'Erro ao re-inscrever',
        description: err?.message || 'Erro desconhecido',
        variant: 'destructive',
      });
      setLastResult({
        ok: false,
        message: `Erro ao re-inscrever: ${err?.message || String(err)}`,
        raw: { error: err?.message },
        at: new Date().toISOString(),
      });
    } finally {
      setSending(false);
    }
  };

  const canTest = isSubscribed && (!isIOS || isStandalone);

  return (
    <div className="bg-white rounded-lg shadow">
      <div className="px-6 py-5 border-b border-gray-100">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-orange-100 text-orange-600 flex items-center justify-center">
            <Bell className="w-5 h-5" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-gray-900">
              Notificações Push (PWA)
            </h3>
            <p className="text-sm text-gray-500">
              Ative e teste as notificações push no seu dispositivo.
            </p>
          </div>
        </div>
      </div>

      <div className="px-6 py-5 space-y-5">
        {/* Status */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <StatusPill
            label="Suportado"
            ok={isSupported}
            okText="Sim"
            failText="Não"
          />
          <StatusPill
            label="Inscrito"
            ok={isSubscribed}
            okText="Ativo"
            failText="Inativo"
          />
          <StatusPill
            label="PWA instalado"
            ok={!isIOS || isStandalone}
            okText={isIOS ? 'Standalone' : 'OK'}
            failText="Necessário no iPhone"
          />
        </div>

        {/* Ativar/Desativar */}
        <div className="flex flex-wrap items-center gap-3">
          <PushNotificationManager />
          <Button
            type="button"
            onClick={handleSendTest}
            disabled={!canTest || sending}
            className="flex items-center gap-2"
          >
            <Send className="w-4 h-4" />
            {sending ? 'Enviando...' : 'Enviar notificação de teste'}
          </Button>
          <Button
            type="button"
            variant="outline"
            onClick={handleResetSubscriptions}
            disabled={sending}
            className="flex items-center gap-2"
          >
            🧹 Limpar e re-inscrever do zero
          </Button>
        </div>
        <p className="text-xs text-gray-500 -mt-2">
          Use "Limpar e re-inscrever" se você tem várias inscrições duplicadas ou se está testando após mudar o VAPID.
        </p>

        {!canTest && isIOS && !isStandalone && (
          <div className="flex items-start gap-2 p-3 rounded-lg bg-amber-50 border border-amber-200 text-sm text-amber-800">
            <Smartphone className="w-4 h-4 mt-0.5 flex-shrink-0" />
            <span>
              No iPhone, instale o app na Tela de Início (Safari → Compartilhar →
              Adicionar à Tela de Início) e abra pelo ícone para poder testar.
            </span>
          </div>
        )}

        {!canTest && !isSubscribed && (!isIOS || isStandalone) && (
          <div className="flex items-start gap-2 p-3 rounded-lg bg-blue-50 border border-blue-200 text-sm text-blue-800">
            <Bell className="w-4 h-4 mt-0.5 flex-shrink-0" />
            <span>
              Clique em <strong>Ativar Push</strong> primeiro para se inscrever
              neste dispositivo, depois envie o teste.
            </span>
          </div>
        )}

        {/* Resultado */}
        {lastResult && (
          <div
            ref={resultRef}
            className={`p-4 rounded-lg border-2 text-sm scroll-mt-20 ${
              lastResult.ok
                ? 'bg-green-50 border-green-400 text-green-900'
                : 'bg-red-50 border-red-400 text-red-900'
            }`}
          >
            <div className="flex items-start gap-2">
              {lastResult.ok ? (
                <CheckCircle2 className="w-4 h-4 mt-0.5 flex-shrink-0" />
              ) : (
                <XCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
              )}
              <div className="flex-1 min-w-0">
                <p className="font-medium">{lastResult.message}</p>

                {Array.isArray(lastResult.raw?.results) && lastResult.raw.results.length > 0 && (
                  <div className="mt-3 overflow-x-auto">
                    <table className="w-full text-xs bg-white/70 rounded">
                      <thead className="text-gray-600">
                        <tr className="border-b border-gray-200">
                          <th className="text-left px-2 py-1">Provedor</th>
                          <th className="text-left px-2 py-1">Status</th>
                          <th className="text-left px-2 py-1">Ação</th>
                          <th className="text-left px-2 py-1">Detalhe</th>
                        </tr>
                      </thead>
                      <tbody className="text-gray-800">
                        {lastResult.raw.results.map((r: any, idx: number) => (
                          <tr key={idx} className="border-b border-gray-100 last:border-0 align-top">
                            <td className="px-2 py-1 font-mono">{r.provider}</td>
                            <td className={`px-2 py-1 font-semibold ${r.success ? 'text-green-700' : 'text-red-700'}`}>
                              {r.status ?? '—'}
                            </td>
                            <td className="px-2 py-1">{r.action}</td>
                            <td className="px-2 py-1 break-words">
                              {r.error || r.body || '—'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}

                <details className="mt-3">
                  <summary className="cursor-pointer text-xs text-gray-600 hover:text-gray-900">
                    Ver JSON completo
                  </summary>
                  <pre className="mt-2 text-xs bg-white/60 p-2 rounded overflow-x-auto whitespace-pre-wrap break-all">
                    {JSON.stringify(lastResult.raw, null, 2)}
                  </pre>
                </details>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const StatusPill: React.FC<{
  label: string;
  ok: boolean;
  okText: string;
  failText: string;
}> = ({ label, ok, okText, failText }) => (
  <div
    className={`flex items-center justify-between px-3 py-2 rounded-lg border ${
      ok
        ? 'bg-green-50 border-green-200'
        : 'bg-gray-50 border-gray-200'
    }`}
  >
    <span className="text-xs font-medium text-gray-600">{label}</span>
    <span
      className={`text-xs font-semibold ${
        ok ? 'text-green-700' : 'text-gray-500'
      }`}
    >
      {ok ? okText : failText}
    </span>
  </div>
);

export default PushTestPanel;
